import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { ethers } from "ethers";
import { useWeb3 } from "../../context/Web3Context";
import { useContract } from "../../hooks/useContract";
import SubTabs from "../../components/SubTabs/SubTabs";
import Loader from "../../components/Loader/Loader";
import StarPackages from "./StarPackages";
import StarIncome from "./StarIncome";
import StarTeam from "./StarTeam";
import { PLAN1_PKG_LABELS } from "../../utils/constants";
import { NETWORK, PLAN1_PKG_COUNT, PLAN1_MATRIX_LEVELS } from "../../config/config";
import { toast } from "react-toastify";
import "./StarTab.css";

const SUB_TABS = [
  { key: "packages", label: "Packages" },
  { key: "income", label: "Income" },
  { key: "team", label: "Team" },
];

export default function StarTab() {
  const { account } = useWeb3();
  const contracts = useContract();
  const [activeTab, setActiveTab] = useState("packages");
  const [userId, setUserId] = useState(null);
  const [prices, setPrices] = useState([]);
  const [packages, setPackages] = useState(null);
  const [incomeStats, setIncomeStats] = useState(null);
  const [pendingRoyalty, setPendingRoyalty] = useState(null);
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (account && contracts) loadData();
    // eslint-disable-next-line
  }, [account, contracts]);

  async function loadData() {
    setPageLoading(true);
    try {
      const uid = await contracts.data.addrToId(account);
      const uidNum = uid.toNumber();
      setUserId(uidNum);
      if (uidNum === 0) { setPageLoading(false); return; }

      const [priceArr, allPkgs, stats, royalty] = await Promise.all([
        contracts.plan1.getPackagePrices(),
        contracts.plan1.getAllPackages(uidNum),
        contracts.plan1.getIncomeStats(uidNum),
        contracts.plan1.getPendingRoyalty(uidNum),
      ]);
      setPrices(priceArr);
      setPackages(allPkgs);
      setIncomeStats({ direct: stats.direct, level: stats.lvl, royalty: stats.royalty });
      setPendingRoyalty(royalty);
    } catch (err) {
      console.error("StarTab load:", err);
      toast.error("Failed to load Star plan data");
    } finally {
      setPageLoading(false);
    }
  }

  async function handleActivate(pkg) {
    if (!contracts || !userId) return;
    setLoadingPkg(pkg);
    try {
      const price = prices[pkg];
      const allowance = await contracts.token.allowance(account, NETWORK.contracts.plan1);
      if (allowance.lt(price)) {
        toast.info("Approving USDT...");
        const tx = await contracts.token.approve(NETWORK.contracts.plan1, ethers.constants.MaxUint256);
        await tx.wait();
        toast.success("USDT approved!");
      }
      // Simulate to get real revert reason if any
      try { await contracts.plan1.callStatic.activatePackage(pkg); }
      catch (sim) { throw new Error(sim.reason || sim.errorName || sim.message || "Simulation failed"); }
      toast.info("Activating package...");
      const tx = await contracts.plan1.activatePackage(pkg);
      await tx.wait();
      toast.success(`${PLAN1_PKG_LABELS[pkg]} activated!`);
      await loadData();
    } catch (err) { toast.error(err.reason || err.message || "Activation failed"); }
    finally { setLoadingPkg(null); }
  }

  async function handleClaimRoyalty() {
    if (!contracts) return;
    setLoadingPkg(-2);
    try {
      const tx = await contracts.plan1.claimRoyalty();
      await tx.wait();
      toast.success("Royalty claimed!");
      await loadData();
    } catch (err) { toast.error(err.reason || err.message || "Royalty claim failed"); }
    finally { setLoadingPkg(null); }
  }

  if (!account) return <Navigate to="/" replace />;
  if (pageLoading) return <Loader message="Loading Star Plan..." />;
  if (userId === 0) return <Navigate to="/register" replace />;

  return (
    <div className="plan-tab">
      <SubTabs tabs={SUB_TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="plan-tab-content">
        {activeTab === "packages" && (
          <StarPackages
            prices={prices}
            packages={packages}
            loadingPkg={loadingPkg}
            pkgCount={PLAN1_PKG_COUNT}
            labels={PLAN1_PKG_LABELS}
            onActivate={handleActivate}
          />
        )}
        {activeTab === "income" && (
          <StarIncome
            incomeStats={incomeStats}
            pendingRoyalty={pendingRoyalty}
            loadingPkg={loadingPkg}
            onClaimRoyalty={handleClaimRoyalty}
            onRefresh={loadData}
          />
        )}
        {activeTab === "team" && (
          <StarTeam
            userId={userId}
            contracts={contracts}
            maxLevels={PLAN1_MATRIX_LEVELS}
          />
        )}
      </div>
    </div>
  );
}
